import { useState, useEffect } from "react";
import axios from "axios";
import { RichTextEditor } from "@/components/RichTextEditor";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MediaLibrary, MediaPicker } from "@/components/admin/MediaLibrary";
import {
  BookOpen, GraduationCap, HelpCircle, CheckCircle2, FileEdit, History,
  Search, Calendar, Plus, Trash2, Pencil, Image as ImageIcon, Megaphone, FolderOpen, X
} from "lucide-react";

const STAT_CARDS = [
  { key: "total_courses", label: "Courses", icon: BookOpen, color: "#6366F1" },
  { key: "total_lessons", label: "Lessons", icon: GraduationCap, color: "#0EA5E9" },
  { key: "total_questions", label: "Questions", icon: HelpCircle, color: "#F59E0B" },
  { key: "total_quizzes", label: "Quizzes", icon: FileEdit, color: "#10B981" },
  { key: "total_media", label: "Media", icon: FolderOpen, color: "#EC4899" },
  { key: "published_content", label: "Published", icon: CheckCircle2, color: "#22C55E" },
  { key: "draft_content", label: "Drafts", icon: Pencil, color: "#94A3B8" },
];

const BLANK_EVENT = {
  name: "", description: "", date: "", start_time: "", end_time: "", location: "",
  mode: "offline", meeting_link: "", registration_link: "", image: "", capacity: "",
  status: "draft", publish_at: "", expire_at: "", audience: "all",
};

export function CmsHub({ onGoToTab }) {
  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [audit, setAudit] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(BLANK_EVENT);
  const [editingId, setEditingId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = async () => {
    if (!token) return;
    try {
      const [s, e, a] = await Promise.all([
        axios.get(`${API}/cms/stats`, { headers: authHeaders }),
        axios.get(`${API}/cms/events`, { headers: authHeaders }),
        axios.get(`${API}/cms/audit`, { headers: authHeaders }),
      ]);
      setStats(s.data);
      setEvents(e.data.events || []);
      setAudit(a.data.audit || []);
    } catch (err) {
      console.error("cms load failed", err?.response?.status);
    }
  };

  useEffect(() => { load(); }, [token]);

  const runSearch = async (q) => {
    setSearch(q);
    if (!q.trim()) { setResults(null); return; }
    try {
      const r = await axios.get(`${API}/cms/search?q=${encodeURIComponent(q)}`, { headers: authHeaders });
      setResults(r.data);
    } catch (err) { /* ignore */ }
  };

  const openNew = () => { setDraft(BLANK_EVENT); setEditingId(null); setDialogOpen(true); };
  const openEdit = (ev) => {
    setDraft({ ...BLANK_EVENT, ...ev });
    setEditingId(ev.event_id);
    setDialogOpen(true);
  };

  const saveEvent = async () => {
    if (!draft.name.trim()) { toast.error("Event name is required"); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/cms/events/${editingId}`, draft, { headers: authHeaders });
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/cms/events`, draft, { headers: authHeaders });
        toast.success("Event created");
      }
      setDialogOpen(false);
      load();
    } catch (err) { toast.error("Couldn't save event"); }
  };

  const togglePublish = async (ev) => {
    const next = ev.status === "published" ? "draft" : "published";
    await axios.put(`${API}/cms/events/${ev.event_id}`, { status: next }, { headers: authHeaders });
    toast.success(next === "published" ? "Published" : "Unpublished");
    load();
  };

  const removeEvent = async (ev) => {
    if (!window.confirm(`Delete event "${ev.name}"?`)) return;
    await axios.delete(`${API}/cms/events/${ev.event_id}`, { headers: authHeaders });
    toast.success("Event deleted");
    load();
  };

  const modules = [
    { label: "Courses", icon: BookOpen, tab: "courses", color: "#6366F1" },
    { label: "Quizzes", icon: FileEdit, tab: "quizzes", color: "#10B981" },
    { label: "Banners", icon: ImageIcon, tab: "banners", color: "#F59E0B" },
    { label: "Site Content", icon: FileEdit, tab: "content", color: "#0EA5E9" },
  ];

  const statusBadge = (st) => {
    const map = { published: "bg-green-100 text-green-700", draft: "bg-slate-100 text-slate-600", archived: "bg-amber-100 text-amber-700" };
    return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${map[st] || map.draft}`}>{(st || "draft").toUpperCase()}</span>;
  };

  return (
    <div className="space-y-6" data-testid="cms-hub">
      <div>
        <h2 className="text-xl font-bold text-foreground">Content Management</h2>
        <p className="text-sm text-muted-foreground">Manage content, events and publishing across TheMatheMind 360.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="cms-stats">
        {STAT_CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key} className="rounded-xl border border-border bg-card p-3" data-testid={`cms-stat-${c.key}`}>
              <Icon className="w-4 h-4 mb-1" style={{ color: c.color }} />
              <p className="text-2xl font-bold text-foreground">{stats ? (stats[c.key] ?? 0) : "—"}</p>
              <p className="text-[11px] text-muted-foreground">{c.label}</p>
            </div>
          );
        })}
      </div>

      {/* Global search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => runSearch(e.target.value)}
          placeholder="Search courses, lessons, quizzes, events, banners, announcements..."
          className="pl-9"
          data-testid="cms-search-input"
        />
        {results && (
          <div className="mt-2 rounded-xl border border-border bg-card p-3 text-sm" data-testid="cms-search-results">
            <p className="text-xs text-muted-foreground mb-2">{results.total} result(s)</p>
            {["courses", "lessons", "quizzes", "events", "banners", "announcements"].map((grp) =>
              (results[grp] || []).length ? (
                <div key={grp} className="mb-2">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{grp}</p>
                  {results[grp].map((r, i) => (
                    <div key={i} className="py-0.5 text-foreground" data-testid={`search-result-${grp}-${i}`}>• {r.title}</div>
                  ))}
                </div>
              ) : null
            )}
            {results.total === 0 && <p className="text-muted-foreground">No matches.</p>}
          </div>
        )}
      </div>

      {/* Module quick-links */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-2">Modules</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {modules.map((m) => {
            const Icon = m.icon;
            return (
              <button
                key={m.tab}
                onClick={() => onGoToTab?.(m.tab)}
                className="rounded-xl border border-border bg-card p-4 text-left hover:shadow-md transition-shadow"
                data-testid={`cms-module-${m.tab}`}
              >
                <Icon className="w-5 h-5 mb-2" style={{ color: m.color }} />
                <p className="text-sm font-semibold text-foreground">{m.label}</p>
                <p className="text-[11px] text-muted-foreground">Open manager</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Media Library */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2"><FolderOpen className="w-4 h-4" /> Media Library</h3>
        <p className="text-xs text-muted-foreground mb-2">Centralised images, videos and PDFs you can reuse across events, banners and lessons.</p>
        <MediaLibrary />
      </div>

      {/* Events */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2"><Calendar className="w-4 h-4" /> Events</h3>
          <Button size="sm" className="rounded-full h-8" onClick={openNew} data-testid="cms-new-event-btn">
            <Plus className="w-4 h-4 mr-1" /> New Event
          </Button>
        </div>
        <div className="rounded-xl border border-border overflow-hidden" data-testid="cms-events-table">
          {events.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="cms-events-empty">No events yet. Create your first event.</div>
          ) : (
            events.map((ev) => (
              <div key={ev.event_id} className="flex items-center gap-3 p-3 border-b border-border last:border-0" data-testid={`cms-event-${ev.event_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-foreground truncate">{ev.name}</p>
                    {statusBadge(ev.status)}
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {ev.date || "No date"} · {ev.mode} · {ev.audience}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-full" onClick={() => togglePublish(ev)} data-testid={`cms-toggle-${ev.event_id}`}>
                  {ev.status === "published" ? "Unpublish" : "Publish"}
                </Button>
                <button onClick={() => openEdit(ev)} className="p-1.5 hover:bg-muted rounded-lg" data-testid={`cms-edit-${ev.event_id}`}><Pencil className="w-4 h-4 text-muted-foreground" /></button>
                <button onClick={() => removeEvent(ev)} className="p-1.5 hover:bg-muted rounded-lg" data-testid={`cms-delete-${ev.event_id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Audit log */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2"><History className="w-4 h-4" /> Recent Activity</h3>
        <div className="rounded-xl border border-border bg-card p-3" data-testid="cms-audit-log">
          {audit.length === 0 ? (
            <p className="text-sm text-muted-foreground">No activity yet.</p>
          ) : (
            audit.map((a) => (
              <div key={a.audit_id} className="text-[13px] text-foreground py-1 border-b border-border/50 last:border-0" data-testid={`audit-${a.audit_id}`}>
                <span className="font-medium">{a.user_name}</span> <span className="text-muted-foreground">{a.action}</span> {a.item_type} <span className="font-medium">"{a.item_title}"</span>
                <span className="text-[10px] text-muted-foreground ml-1">{(a.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Event editor dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="cms-event-dialog">
          <DialogHeader><DialogTitle>{editingId ? "Edit Event" : "New Event"}</DialogTitle>
            <DialogDescription>Fill in the event details. Drafts stay hidden from students until published.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Event name *"><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="event-name-input" /></Field>
            <Field label="Description"><RichTextEditor value={draft.description || ""} onChange={(html) => setDraft((d) => ({ ...d, description: html }))} minHeight={90} testId="event-desc-input" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date"><Input type="date" value={draft.date || ""} onChange={(e) => setDraft({ ...draft, date: e.target.value })} data-testid="event-date-input" /></Field>
              <Field label="Capacity"><Input value={draft.capacity || ""} onChange={(e) => setDraft({ ...draft, capacity: e.target.value })} placeholder="e.g. 100" /></Field>
              <Field label="Start time"><Input type="time" value={draft.start_time || ""} onChange={(e) => setDraft({ ...draft, start_time: e.target.value })} /></Field>
              <Field label="End time"><Input type="time" value={draft.end_time || ""} onChange={(e) => setDraft({ ...draft, end_time: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Mode">
                <Select value={draft.mode} onValueChange={(v) => setDraft({ ...draft, mode: v })}>
                  <SelectTrigger data-testid="event-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="offline">Offline</SelectItem><SelectItem value="online">Online</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Audience">
                <Select value={draft.audience} onValueChange={(v) => setDraft({ ...draft, audience: v })}>
                  <SelectTrigger data-testid="event-audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All users</SelectItem>
                    <SelectItem value="students">Students</SelectItem>
                    <SelectItem value="teachers">Teachers</SelectItem>
                    <SelectItem value="parents">Parents</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label={draft.mode === "online" ? "Meeting link" : "Location"}>
              <Input value={draft.mode === "online" ? (draft.meeting_link || "") : (draft.location || "")}
                onChange={(e) => setDraft(draft.mode === "online" ? { ...draft, meeting_link: e.target.value } : { ...draft, location: e.target.value })} />
            </Field>
            <Field label="Registration link"><Input value={draft.registration_link || ""} onChange={(e) => setDraft({ ...draft, registration_link: e.target.value })} /></Field>
            <Field label="Event image">
              <div className="flex items-center gap-3">
                {draft.image ? (
                  <div className="relative w-20 h-14 rounded-lg overflow-hidden border border-border shrink-0">
                    <img src={draft.image} alt="event" className="w-full h-full object-cover" data-testid="event-image-preview" />
                    <button type="button" onClick={() => setDraft({ ...draft, image: "" })} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center" data-testid="event-image-remove"><X className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <div className="w-20 h-14 rounded-lg border border-dashed border-border flex items-center justify-center shrink-0 text-muted-foreground"><ImageIcon className="w-5 h-5" /></div>
                )}
                <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => setPickerOpen(true)} data-testid="event-pick-media-btn">
                  <FolderOpen className="w-4 h-4 mr-1" /> Pick from Library
                </Button>
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Publish at (optional)"><Input type="datetime-local" value={draft.publish_at || ""} onChange={(e) => setDraft({ ...draft, publish_at: e.target.value })} data-testid="event-publishat-input" /></Field>
              <Field label="Expire at (optional)"><Input type="datetime-local" value={draft.expire_at || ""} onChange={(e) => setDraft({ ...draft, expire_at: e.target.value })} data-testid="event-expireat-input" /></Field>
            </div>
            <Field label="Status">
              <Select value={draft.status} onValueChange={(v) => setDraft({ ...draft, status: v })}>
                <SelectTrigger data-testid="event-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveEvent} data-testid="event-save-btn">{editingId ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MediaPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={(item) => setDraft((d) => ({ ...d, image: item.url }))}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 mb-1">{label}</p>
      {children}
    </div>
  );
}
